import React, { FormEvent, useEffect, useState } from 'react'
import { graphql, withApollo } from 'react-apollo'
import { Redirect, useHistory, withRouter } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import {
  CheckboxInput,
  ConfirmationModal,
  ModalActionTypes,
  Spinner,
  VerificationInput,
} from '@acorns/web-components'
import { withAnalyticsClient } from '@acorns/web-utils'
import { useFeature } from '@optimizely/react-sdk'
import { Form, FormikActions, withFormik } from 'formik'
import { ExecutionResult } from 'graphql'
import { compose, withHandlers, withProps, withState } from 'recompose'
import styled from 'styled-components'

import { AuthType, AuthenticateMutation } from 'generated/graphql'
import { withEnv } from 'utils/environment'
import { trustThisDeviceWeb } from 'utils/experiments/features'
import { hasUdid } from 'utils/udid-utils'
import withAnalyticsIdentity from 'utils/with-analytics-identity'

import Layout from '../../components/Layout'
import {
  ExpiredCodeModalViewed,
  HelpButtonTapped,
  HelpModalViewed,
  ResendButtonTapped,
} from '../../utils/segment'
import {
  BackButtonContainer,
  BackButtonStyled,
  BottomContainer,
  HeaderSubcopy,
  HeaderSubcopyBolded,
  NeedHelpButton,
  ResendButton,
  SubmitButton,
  SwitchModeButton,
  SwitchModeContainer,
  SwitchModeIcon,
  TrustThisDeviceContainer,
} from './components'
import {
  Values,
  handleFormSubmit,
  mapPropsToValues,
  validationSchema,
} from './form'
import ChallengeAuthenticate from './queries/challenge-authenticate.gql'
import ReinitiateChallenge from './queries/resend-challenge-code.gql'

export type Props = {
  values: Values
  history: History
  isSubmitting: boolean
  isValid: boolean
  isModalOpen: boolean
  isHelpModalOpen: boolean
  isHavingTroubleModalOpen: boolean
  isExpiredModalOpen: boolean
  phoneNumber: string
  location: any
  resendChallengeId: string
  analytics: any
  resendClickCounter: number
  isSwitchingAuthenticator: boolean
  isResendingChallenge: boolean
  trustThisDeviceWebIsEnabled: boolean
  setResendClickCounter: (resendClickCounter: number) => void
  setIsModalOpen: (value: boolean) => void
  setResendchallengeId: (value: string) => void
  setIsHelpModalOpen: (value: boolean) => void
  setIsHavingTroubleModalOpen: (value: boolean) => void
  setIsExpiredModalOpen: (value: boolean) => void
  setIsSwitchingAuthenticator: (value: boolean) => void
  onBackPress: () => void
  closeModal: () => void
  handleHelpModal: () => void
  handleResendChallengeCode: (challengeId: string) => void
  handleChange: (event: FormEvent<HTMLInputElement>) => void
  handleSubmit: () => void
  handleSwitchingAuthenticator: (
    values: any,
    alternateAuthenticatorId: string,
    history: History,
  ) => void
  handleBlur: (event: FormEvent<HTMLInputElement>) => void
  reset: (options?: any) => any
  handleCloseExpiredModal: () => void
} & FormikActions<{ challengeAnswer: string }>

const BUTTON_TAPPED = 'Button Tapped'
const MAX_LENGTH = 6

export const enhance = compose<Props, {}>(
  withAnalyticsClient,
  withAnalyticsIdentity,
  withRouter,
  withApollo,
  withEnv,
  graphql(ChallengeAuthenticate, { name: 'login' }),
  withState('isModalOpen', 'setIsModalOpen', false),
  withState('isHelpModalOpen', 'setIsHelpModalOpen', false),
  withState('isHavingTroubleModalOpen', 'setIsHavingTroubleModalOpen', false),
  withState('isExpiredModalOpen', 'setIsExpiredModalOpen', false),
  withState('resendChallengeId', 'setResendChallengeId', ''),
  withState('resendClickCounter', 'setResendClickCounter', 0),
  withState('isSwitchingAuthenticator', 'setIsSwitchingAuthenticator', false),
  withState('isResendingChallenge', 'setIsResendingChallenge', false),
  withProps(() => {
    const [trustThisDeviceWebIsEnabled] = useFeature(trustThisDeviceWeb)
    return { trustThisDeviceWebIsEnabled }
  }),
  withFormik({
    validationSchema,
    mapPropsToValues,
    handleSubmit: handleFormSubmit,
  }),
  withHandlers({
    onBackPress:
      ({ history }) =>
      () => {
        const url = document.referrer.split('/')
        if (url[3] === 'redeem') {
          history.push('/redeem')
        } else {
          history.push('/')
        }
      },
    handleResendChallengeCode:
      ({
        analytics,
        client,
        resendClickCounter,
        setResendChallengeId,
        setIsExpiredModalOpen,
        setIsResendingChallenge,
        setResendClickCounter,
        setIsHavingTroubleModalOpen,
      }) =>
      (challengeId: string) => {
        setIsResendingChallenge(true)
        analytics.track(BUTTON_TAPPED, ResendButtonTapped('Resend'))
        setResendClickCounter(resendClickCounter + 1)
        if (resendClickCounter === 2) {
          setIsHavingTroubleModalOpen(true)
          setResendClickCounter(0)
        } else {
          return client
            .mutate({
              mutation: ReinitiateChallenge,
              variables: {
                input: { challengeId },
              },
            })
            .then((result) => {
              if (
                result.data.reinitiateChallenge.__typename ===
                'AuthChallengeExpiredException'
              ) {
                setIsExpiredModalOpen(true)
                analytics.track('Container Viewed', ExpiredCodeModalViewed)
              } else {
                setResendChallengeId(result.data.reinitiateChallenge.id)
              }
            })
            .finally(() => {
              setIsResendingChallenge(false)
            })
        }
      },

    handleSwitchingAuthenticator:
      ({
        analytics,
        client,
        setIsExpiredModalOpen,
        setIsSwitchingAuthenticator,
      }) =>
      (values: any, alternateAuthenticatorId: string, history) => {
        setIsSwitchingAuthenticator(true)
        analytics.track(
          BUTTON_TAPPED,
          ResendButtonTapped('Switch Authenticators'),
        )
        client
          .mutate({
            mutation: ChallengeAuthenticate,
            variables: {
              input: {
                ...values,
              },
              alternateAuthenticatorId,
            },
          })
          .then((result: ExecutionResult<AuthenticateMutation>) => {
            if (result.data.authenticate.__typename === 'SMSAuthChallenge') {
              const mode = AuthType.Phone
              const challengeId = result.data.authenticate.id
              const phoneNumber =
                '(•••) ••• ' + result.data.authenticate.maskedPhoneNumber
              const altAuthenticatorId =
                result.data.authenticate.alternateAuthenticators.find(
                  (authenticator) => authenticator.type !== mode,
                )?.id

              history.push({
                pathname: '/mfa-challenge',
                data: {
                  values,
                  challengeId,
                  phoneNumber,
                  mode,
                  alternateAuthenticatorId: altAuthenticatorId,
                  preservedRedirect: history.location.data.preservedRedirect,
                },
              })
            } else if (
              result.data.authenticate.__typename === 'EmailAuthChallenge'
            ) {
              const mode = AuthType.Email
              const challengeId = result.data.authenticate.id
              const email = result.data.authenticate.maskedEmail
              const altAuthenticatorId =
                result.data.authenticate.alternateAuthenticators.find(
                  (authenticator) => authenticator.type !== mode,
                )?.id

              history.push({
                pathname: '/mfa-challenge',
                data: {
                  values,
                  challengeId,
                  email,
                  mode,
                  alternateAuthenticatorId: altAuthenticatorId,
                  preservedRedirect: history.location.data.preservedRedirect,
                },
              })
            } else {
              setIsExpiredModalOpen(true)
            }
          })
          .finally(() => setIsSwitchingAuthenticator(false))
      },

    closeModal:
      ({ setIsModalOpen, setIsHavingTroubleModalOpen }) =>
      () => {
        setIsModalOpen(false)
        setIsHavingTroubleModalOpen(false)
      },
    handleHelpModal:
      ({ analytics, setIsHelpModalOpen, isHelpModalOpen }) =>
      () => {
        if (isHelpModalOpen) {
          setIsHelpModalOpen(false)
        } else {
          analytics.track(BUTTON_TAPPED, HelpButtonTapped('Need Help'))
          setIsHelpModalOpen(true)
          analytics.track('Container Viewed', HelpModalViewed)
        }
      },
    handleCloseExpiredModal:
      ({ setIsExpiredModalOpen, history }) =>
      () => {
        setIsExpiredModalOpen(false)
        history.push('/')
      },
  }),
)

const VerificationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 auto;
  text-align: center;
`

export const MfaChallenge = ({
  onBackPress,
  handleSubmit,
  handleChange,
  handleBlur,
  values,
  isSubmitting,
  isValid,
  isModalOpen,
  isHelpModalOpen,
  isExpiredModalOpen,
  isHavingTroubleModalOpen,
  closeModal,
  handleHelpModal,
  handleCloseExpiredModal,
  handleResendChallengeCode,
  handleSwitchingAuthenticator,
  location,
  isSwitchingAuthenticator,
  isResendingChallenge,
  trustThisDeviceWebIsEnabled,
}: Props) => {
  const history = useHistory()
  const [mode, setMode] = useState<AuthType>(location?.data?.mode)
  const [masked, setMasked] = useState(
    location?.data?.mode === AuthType.Phone
      ? location?.data?.phoneNumber
      : location?.data?.email,
  )

  const actionText = mode === AuthType.Phone ? 'phone number' : 'email'
  const helpText = `If you are having trouble verifying your ${actionText}, please get in touch with our support team: 855-739-2859`

  useEffect(() => {
    if (isSwitchingAuthenticator) {
      setMode(mode === AuthType.Phone ? AuthType.Email : AuthType.Phone)
    }
  }, [isSwitchingAuthenticator])

  useEffect(() => {
    setMasked(
      location?.data?.mode === AuthType.Phone
        ? location?.data?.phoneNumber
        : location?.data?.email,
    )
  }, [location?.data])

  const SwitchModeButtonContent = ({ authMode }) => {
    if (authMode === AuthType.Phone) {
      return (
        <>
          <SwitchModeIcon icon={AcornsIcon.Icon.MfaEmailIcon} width="15px" />{' '}
          Email my code instead
        </>
      )
    } else {
      return (
        <>
          <SwitchModeIcon icon={AcornsIcon.Icon.MfaPhoneIcon} width="15px" />{' '}
          Text my code instead
        </>
      )
    }
  }

  return location.data ? (
    <Layout copy="Enter Code">
      <HeaderSubcopy>
        We sent a code to <HeaderSubcopyBolded>{masked}</HeaderSubcopyBolded>.
        Enter it here to confirm your identity.
      </HeaderSubcopy>
      <ResendButton
        styleType="flat"
        primary
        onPress={() => handleResendChallengeCode(location.data.challengeId)}
      >
        {isResendingChallenge ? (
          <Spinner height={20} width={20} />
        ) : (
          <>Resend code</>
        )}
      </ResendButton>
      <BackButtonContainer>
        <BackButtonStyled onClick={onBackPress} />
      </BackButtonContainer>
      <Form name="MFA-form">
        <VerificationContainer>
          <VerificationInput
            autoFocus={true}
            autocomplete="one-time-code"
            id="ChallengeAnswer"
            name="challengeAnswer"
            label={'what was the code sent to you?'}
            onChange={handleChange}
            value={values.challengeAnswer}
            maxLength={MAX_LENGTH}
            type="text"
            pattern={`[0-9]{${MAX_LENGTH}}`}
          />
        </VerificationContainer>
        <BottomContainer>
          <SwitchModeContainer>
            {location.data.alternateAuthenticatorId && (
              <SwitchModeButton
                styleType="flat"
                secondary
                onPress={() =>
                  handleSwitchingAuthenticator(
                    location.data.values,
                    location.data.alternateAuthenticatorId,
                    history,
                  )
                }
                disabled={isSwitchingAuthenticator}
              >
                {isSwitchingAuthenticator ? (
                  <Spinner height={20} width={20} />
                ) : (
                  <SwitchModeButtonContent authMode={mode} />
                )}
              </SwitchModeButton>
            )}
          </SwitchModeContainer>
          <TrustThisDeviceContainer>
            {trustThisDeviceWebIsEnabled && hasUdid() && (
              <CheckboxInput
                id="TrustThisDevice"
                name="trustThisDevice"
                label="Trust This Device"
                onChange={handleChange}
                checked={values.trustThisDevice}
                disabled={isSubmitting}
              />
            )}
          </TrustThisDeviceContainer>
          <SubmitButton
            buttonType="submit"
            disabled={isSubmitting || !isValid}
            onPress={handleSubmit}
            name="submit-button"
          >
            Confirm
          </SubmitButton>
          <NeedHelpButton styleType="flat" secondary onClick={handleHelpModal}>
            Need Help?
          </NeedHelpButton>
        </BottomContainer>
      </Form>
      <ConfirmationModal
        isOpen={isModalOpen}
        actions={[
          {
            handler: closeModal,
            label: 'Try Again',
            type: ModalActionTypes.primary,
          },
        ]}
        title="Unable to verify"
        message="Looks like the code has expired or does not match"
        image={<AcornsIcon icon={AcornsIcon.Icon.AlertBlack} width="80px" />}
      />
      <ConfirmationModal
        isOpen={isHelpModalOpen}
        actions={[
          {
            handler: handleHelpModal,
            label: 'Ok',
            type: ModalActionTypes.secondary,
          },
        ]}
        title="Need help?"
        message={helpText}
        image={<AcornsIcon icon={AcornsIcon.Icon.AlertBlack} width="80px" />}
      />
      <ConfirmationModal
        isOpen={isExpiredModalOpen}
        actions={[
          {
            handler: handleCloseExpiredModal,
            label: 'Ok',
            type: ModalActionTypes.primary,
          },
        ]}
        title="Session Expired"
        message="For your security, your session has expired and you will need to log in again to continue."
        image={<AcornsIcon icon={AcornsIcon.Icon.AlertBlack} width="80px" />}
      />
      <ConfirmationModal
        isOpen={isHavingTroubleModalOpen}
        actions={[
          {
            handler: closeModal,
            label: 'Ok',
            type: ModalActionTypes.primary,
          },
        ]}
        title="Having Trouble?"
        message="It looks like you're not receiving the text messages we are sending.
          Please verify your phone number is correct, and is a number that can receive text messages"
        image={<AcornsIcon icon={AcornsIcon.Icon.AlertBlack} width="80px" />}
      />
    </Layout>
  ) : (
    <Redirect to="/" />
  )
}

export default enhance(MfaChallenge)
